import { useContext, useEffect, useState } from "react";
import { Button, Input, Table, Tags } from "../../../ui";
import { Context } from "../../../../context/Context";
import { useAccess } from "../../../../access/hooks/useAccess";
import { formatSar } from "../../../../utils/billingMoney";
import {
  applyCouponToInvoiceAPI,
  approvePaymentAPI,
  cancelPaymentAPI,
  createManualPaymentAPI,
  downloadInvoicePdfAPI,
  getInvoiceHistoryAPI,
  getPaymentReceiptUrlAPI,
  listBillingStoresAPI,
  listInvoicesAPI,
  rejectPaymentAPI,
  voidInvoiceAPI,
} from "../../../../utils/api/billing";
import ManualPaymentModal from "./ManualPaymentModal";
import { labelFor, useBillingAdminText } from "../billingAdminI18n";

const formatDateTime = (value, lang) =>
  value ? new Date(value).toLocaleString(lang === "ar" ? "ar-SA" : "en-US") : "-";

const actionLabel = (text, action) => text.history.actionLabels[String(action || "").replace(/\./g, "_")] || action;

const InvoiceDetailModal = ({ invoice, onClose, onUpdated, canReviewPayment = true, canVoidInvoice = true, canIssueInvoice = true }) => {
  const { lang } = useContext(Context);
  const { can } = useAccess();
  const text = useBillingAdminText(lang);
  const token = localStorage.getItem("auth-token");
  const [couponCode, setCouponCode] = useState("");
  const [voidReason, setVoidReason] = useState("");
  const [showVoid, setShowVoid] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [history, setHistory] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showRecordPayment, setShowRecordPayment] = useState(false);

  useEffect(() => {
    if (!showHistory || history) return;
    getInvoiceHistoryAPI(token, invoice._id)
      .then((result) => setHistory(result.data || []))
      .catch((err) => setError(err.message));
  }, [showHistory, history, token, invoice._id]);

  const pendingPayments = (invoice.payments || []).filter((payment) => payment.status === "pending_review");

  // A final payment decision is confirmed first; rejecting or cancelling needs a typed reason (the client sees it).
  const [decision, setDecision] = useState(null); // { paymentId, action }
  const [decisionReason, setDecisionReason] = useState("");
  const confirmDecision = async () => {
    const needsReason = decision.action !== "approve";
    if (needsReason && !decisionReason.trim()) {
      setError(text.payments.reasonRequired);
      return;
    }
    await reviewPayment(decision.paymentId, decision.action, decisionReason.trim());
    setDecision(null);
    setDecisionReason("");
  };

  const reviewPayment = async (paymentId, action, reason) => {
    if (!can("billing.admin.invoice.issue")) return;
    setBusy(`${action}:${paymentId}`);
    setError("");
    try {
      if (action === "approve") await approvePaymentAPI(token, paymentId);
      else if (action === "reject") await rejectPaymentAPI(token, paymentId, { reason });
      else await cancelPaymentAPI(token, paymentId, { reason });
      onUpdated?.(invoice);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy("");
    }
  };

  const exportPdf = async () => {
    setBusy("pdf");
    setError("");
    try {
      await downloadInvoicePdfAPI(token, invoice._id, lang);
    } catch (err) {
      setError(err.message || text.invoices.exportPdfError);
    } finally {
      setBusy("");
    }
  };

  const openReceipt = async (payment) => {
    setBusy(`receipt:${payment._id}`);
    setError("");
    try {
      const result = await getPaymentReceiptUrlAPI(token, payment._id);
      window.open(result.data.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(err.message || text.payments.receiptError);
    } finally {
      setBusy("");
    }
  };

  const applyCoupon = async () => {
    if (!can("billing.admin.invoice.issue")) return;
    if (!couponCode.trim()) return;
    setBusy("coupon");
    setError("");
    try {
      const result = await applyCouponToInvoiceAPI(token, invoice._id, { code: couponCode.trim() });
      onUpdated?.(result.data);
      setCouponCode("");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy("");
    }
  };

  const voidInvoice = async () => {
    if (!can("billing.admin.invoice.issue")) return;
    if (!voidReason.trim()) {
      setError(text.invoices.voidReasonRequired);
      return;
    }
    setBusy("void");
    setError("");
    try {
      const result = await voidInvoiceAPI(token, invoice._id, { reason: voidReason });
      onUpdated?.(result.data);
      setShowVoid(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy("");
    }
  };

  const columns = [
    { key: "type", label: text.invoices.type, render: (item) => labelFor(text.types, item.type) },
    { key: "description", label: text.invoices.descriptionLabel, render: (item) => item.descriptionEn || item.descriptionAr || "-" },
    { key: "quantity", label: text.invoices.quantity },
    { key: "unitPriceMinor", label: text.invoices.unit, render: (item) => formatSar(item.unitPriceMinor, lang) },
    { key: "discountMinor", label: text.common.discount, render: (item) => formatSar(item.discountMinor || 0, lang) },
    { key: "vatAmountMinor", label: text.common.vat, render: (item) => formatSar(item.vatAmountMinor, lang) },
    { key: "totalMinor", label: text.common.total, render: (item) => formatSar(item.totalMinor, lang) },
  ];

  const paymentColumns = [
    { key: "amountMinor", label: text.payments.amount, render: (payment) => formatSar(payment.amountMinor, lang) },
    { key: "method", label: text.payments.method, render: (payment) => labelFor(text.methods, payment.method) },
    { key: "reference", label: text.payments.reference, render: (payment) => payment.reference || "-" },
    { key: "status", label: text.common.status, render: (payment) => <Tags>{labelFor(text.statuses, payment.status)}</Tags> },
    { key: "createdAt", label: text.payments.submitted, render: (payment) => formatDateTime(payment.createdAt, lang) },
    {
      key: "receipt",
      label: "",
      render: (payment) =>
        payment.receiptFile ? (
          <Button
            size="sm"
            appearance="outline"
            loading={busy === `receipt:${payment._id}`}
            onClick={() => openReceipt(payment)}
          >
            {text.payments.viewReceipt}
          </Button>
        ) : (
          "-"
        ),
    },
  ];

  const canCancelInvoice = canVoidInvoice && ["draft", "issued"].includes(invoice.status) && (invoice.paidAmountMinor || 0) <= 0;
  const canApplyCoupon = canIssueInvoice && invoice.status === "issued" && invoice.paymentStatus === "unpaid" && (invoice.paidAmountMinor || 0) === 0 && !invoice.couponSnapshot;

  return (
    <div className="billing-modal-backdrop">
      <div className="billing-modal billing-modal-wide">
        <h3>{invoice.invoiceNumber || text.invoices.detailDraftTitle}</h3>
        <p>{invoice.storeId}</p>
        {error && <div className="billing-error" role="alert">{error}</div>}
        <div className="billing-version-groups">
          <div>
            <span>{text.common.status}</span>
            <strong><Tags>{labelFor(text.statuses, invoice.status)}</Tags></strong>
          </div>
          <div>
            <span>{text.invoices.payment}</span>
            <strong>{labelFor(text.statuses, invoice.paymentStatus)}</strong>
          </div>
          <div>
            <span>{text.invoices.billingName}</span>
            <strong>{invoice.billingDetailsSnapshot?.billingNameEn || invoice.billingDetailsSnapshot?.billingNameAr || "-"}</strong>
          </div>
          <div>
            <span>{text.invoices.issueDate}</span>
            <strong>{formatDateTime(invoice.issueDate, lang)}</strong>
          </div>
          <div>
            <span>{text.invoices.dueDate}</span>
            <strong>{formatDateTime(invoice.dueDate, lang)}</strong>
          </div>
        </div>

        <Table columns={columns} data={invoice.items || []} emptyText={text.invoices.noItems} />

        <div className="billing-preview-box">
          <div><span>{text.common.subtotal}</span><strong>{formatSar(invoice.subtotalMinor, lang)}</strong></div>
          <div><span>{text.common.discount}</span><strong>{formatSar(invoice.discountAmountMinor, lang)}</strong></div>
          <div><span>{text.common.taxable}</span><strong>{formatSar(invoice.taxableAmountMinor, lang)}</strong></div>
          <div><span>{text.common.vat}</span><strong>{formatSar(invoice.vatAmountMinor, lang)}</strong></div>
          <div><span>{text.common.total}</span><strong>{formatSar(invoice.totalMinor, lang)}</strong></div>
          <div><span>{text.common.paid}</span><strong>{formatSar(invoice.paidAmountMinor || 0, lang)}</strong></div>
          <div><span>{text.common.remaining}</span><strong>{formatSar(invoice.balanceDueMinor || 0, lang)}</strong></div>
        </div>

        {invoice.couponSnapshot && (
          <div className="billing-preview-box">
            <strong>{text.invoices.detail.couponApplied}</strong>
            <span>
              {invoice.couponSnapshot.code} · {invoice.couponSnapshot.type === "percent" ? `${invoice.couponSnapshot.value}%` : formatSar(invoice.couponSnapshot.value, lang)} · -{formatSar(invoice.couponSnapshot.discountAmountMinor, lang)}
            </span>
          </div>
        )}
        {canApplyCoupon && (
          <div className="billing-inline-form">
            <Input
              label={text.invoices.detail.couponCode}
              value={couponCode}
              onChange={(event) => setCouponCode(event.target.value)}
              placeholder="SAVE10"
            />
            <Button appearance="outline" loading={busy === "coupon"} onClick={applyCoupon}>
              {text.invoices.detail.applyCoupon}
            </Button>
          </div>
        )}

        <h4>{text.invoices.detail.paymentReview}</h4>
        {canReviewPayment && ["issued", "partially_paid", "overdue"].includes(invoice.status) && (invoice.balanceDueMinor || 0) > 0 && (
          <Button size="sm" appearance="outline" onClick={() => setShowRecordPayment(true)}>
            {text.payments.record}
          </Button>
        )}
        {canReviewPayment && pendingPayments.length > 0 ? (
          pendingPayments.map((payment) => (
            <div key={payment._id} className="billing-inline-form">
              <span>{formatSar(payment.amountMinor, lang)} · {payment.reference || "-"}</span>
              <Button size="sm" appearance="filled" loading={busy === `approve:${payment._id}`} onClick={() => { setError(""); setDecision({ paymentId: payment._id, action: "approve" }); }}>
                {text.payments.approve}
              </Button>
              <Button size="sm" appearance="outline" loading={busy === `reject:${payment._id}`} onClick={() => { setError(""); setDecision({ paymentId: payment._id, action: "reject" }); }}>
                {text.payments.reject}
              </Button>
              <Button size="sm" appearance="ghost" loading={busy === `cancel:${payment._id}`} onClick={() => { setError(""); setDecision({ paymentId: payment._id, action: "cancel" }); }}>
                {text.payments.cancel}
              </Button>
            </div>
          ))
        ) : (
          <div className="billing-empty-note">{text.invoices.detail.noPendingPayments}</div>
        )}
        {decision && (
          <div className="billing-inline-form" role="group" aria-label={text.payments.confirmAction}>
            <p>{decision.action === "approve" ? text.payments.confirmApprove : decision.action === "reject" ? text.payments.confirmReject : text.payments.confirmCancel}</p>
            {decision.action !== "approve" && (
              <Input label={text.payments.reasonLabel} value={decisionReason} onChange={(event) => setDecisionReason(event.target.value)} />
            )}
            <Button size="sm" appearance="filled" loading={Boolean(busy)} onClick={confirmDecision}>
              {text.payments.confirmAction}
            </Button>
            <Button size="sm" appearance="ghost" onClick={() => { setDecision(null); setDecisionReason(""); setError(""); }}>
              {text.payments.backAction}
            </Button>
          </div>
        )}

        <h3>{text.invoices.paymentHistory}</h3>
        <Table columns={paymentColumns} data={invoice.payments || []} emptyText={text.invoices.noPayments} />

        <button type="button" className="billing-inline-toggle" onClick={() => setShowHistory((value) => !value)}>
          {text.invoices.detail.historyTitle}
        </button>
        {showHistory && (
          <div className="billing-history-panel">
            {history === null ? (
              <div className="billing-loading">{text.common.loading}</div>
            ) : history.length === 0 ? (
              <div className="billing-empty-note">{text.invoices.detail.historyEmpty}</div>
            ) : (
              history.map((row) => (
                <div key={row.id} className="billing-history-row">
                  <strong>{actionLabel(text, row.action)}</strong>
                  <span>{formatDateTime(row.createdAt, lang)}</span>
                  {row.reason && <small>{row.reason}</small>}
                </div>
              ))
            )}
          </div>
        )}

        {showVoid && (
          <div className="billing-inline-form">
            <Input label={text.common.reason} value={voidReason} onChange={(event) => setVoidReason(event.target.value)} placeholder={text.invoices.voidReasonPlaceholder} />
            <Button appearance="filled" variant="danger" loading={busy === "void"} onClick={voidInvoice}>
              {text.invoices.void}
            </Button>
          </div>
        )}

        <div className="billing-modal-actions">
          {invoice.invoiceNumber && (
            <Button appearance="outline" loading={busy === "pdf"} onClick={exportPdf}>
              {text.invoices.exportPdf}
            </Button>
          )}
          {canCancelInvoice && !showVoid && (
            <Button appearance="ghost" variant="danger" onClick={() => setShowVoid(true)}>
              {text.invoices.void}
            </Button>
          )}
          <Button appearance="filled" onClick={onClose}>
            {text.common.close}
          </Button>
        </div>
      </div>
      {showRecordPayment && (
        <ManualPaymentModal
          loadStores={listBillingStoresAPI}
          loadInvoices={listInvoicesAPI}
          createPayment={createManualPaymentAPI}
          onCancel={() => setShowRecordPayment(false)}
          onCreated={async () => {
            setShowRecordPayment(false);
            onUpdated?.(invoice);
          }}
        />
      )}
    </div>
  );
};

export default InvoiceDetailModal;
